const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.memegenes.com';

/**
 * A free original from the MemeGenes gallery feed — a published, verified work
 * that is not for sale (no price, no edition). Fields can be null; consumers
 * must guard. `publicMemeId` is the Gene id used to link out to MemeGenes.
 */
export type GalleryItem = {
  imageUrl: string;
  publicMemeId: string;
  walletAddress: string | null;
  views: number;
  votes: number;
  createdAt: number;
  displayName: string | null;
  twitterHandle: string | null;
  profileImageUrl: string | null;
  creator_x_handle: string | null;
  creator_has_x: boolean;
};

/** A person credited on an original — creator or current owner. */
export type Person = {
  displayName: string | null;
  username: string | null;
  profileImageUrl: string | null;
};

/** Where an original has appeared. */
export type Sighting = {
  url: string | null;
  platform: string | null;
  timestamp: number | null;
};

/**
 * An original's editorial detail, mapped from `GET /api/g/{id}`. Provny shows
 * this editorially and links out to the Gene for full provenance — it does not
 * reproduce raw on-chain history. Most fields can be null; consumers must guard.
 */
export type OriginalDetail = {
  id: string;
  imageUrl: string | null;
  derivationType: string | null;
  socialPostUrl: string | null;
  postStatus: string | null;
  creator: Person;
  createdAt: number | null;
  sightingsCount: number;
  platformCount: number;
  childrenCount: number;
};

type FeedResponse = {
  items: GalleryItem[];
  nextCursor: string | null;
  hasMore: boolean;
};

const EMPTY: FeedResponse = { items: [], nextCursor: null, hasMore: false };

/**
 * Feed sort order, matching MemeGenes' Explore. The UI exposes four labels but
 * the backend only takes three values — "Top" (engagement) maps to "popular",
 * the same as MemeGenes' Explore.
 */
export type SortOption = 'recent' | 'popular' | 'views' | 'engagement';

/** Map a UI sort option to the `sort_by` value the gallery feed accepts. */
export function sortByParam(sort: SortOption): 'recent' | 'popular' | 'views' {
  return sort === 'engagement' ? 'popular' : sort;
}

/**
 * Fetch a page of free originals from the public gallery feed. Fail-open: on any
 * network/HTTP/parse error, returns an empty page rather than throwing, so a
 * backend outage can never break the surfaces that render it.
 */
export async function fetchGalleryFeed(
  opts: { limit?: number; cursor?: string; sort?: SortOption } = {},
): Promise<FeedResponse> {
  try {
    const params = new URLSearchParams();
    if (opts.limit != null) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    if (opts.sort) params.set('sort_by', sortByParam(opts.sort));
    const qs = params.toString();
    const res = await fetch(`${API_BASE}/api/gallery/feed${qs ? `?${qs}` : ''}`);
    if (!res.ok) return EMPTY;
    const data = (await res.json()) as {
      items?: GalleryItem[];
      next_cursor?: string | null;
      has_more?: boolean;
    };
    return {
      items: Array.isArray(data.items) ? data.items : [],
      nextCursor: data.next_cursor ?? null,
      hasMore: data.has_more === true,
    };
  } catch {
    return EMPTY;
  }
}

/** Distinct, non-empty platform count across an original's sightings. */
export function distinctPlatformCount(sightings: Sighting[]): number {
  const set = new Set<string>();
  for (const s of sightings) if (s.platform) set.add(s.platform);
  return set.size;
}

/**
 * Fetch a single original's editorial detail by its public Gene id. Fail-soft:
 * on 404, any HTTP/network/parse error, returns `null` so the page can render a
 * not-found state rather than throwing.
 */
export async function fetchOriginal(id: string): Promise<OriginalDetail | null> {
  try {
    const res = await fetch(`${API_BASE}/api/g/${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    const data = (await res.json()) as {
      root_id?: string | null;
      image_url?: string | null;
      derivation_type?: string | null;
      social_post_url?: string | null;
      post_status?: string | null;
      creator?: string | null;
      creator_info?: {
        display_name?: string | null;
        username?: string | null;
        profile_image_url?: string | null;
      } | null;
      tree?: {
        created_at?: number | null;
        sightings_count?: number | null;
        children?: unknown[] | null;
      } | null;
      sightings?: Sighting[] | null;
    };

    const ci = data.creator_info ?? null;
    const sightings = Array.isArray(data.sightings) ? data.sightings : [];

    return {
      id: data.root_id ?? id,
      imageUrl: data.image_url ?? null,
      derivationType: data.derivation_type ?? null,
      socialPostUrl: data.social_post_url ?? null,
      postStatus: data.post_status ?? null,
      creator: {
        displayName: ci?.display_name ?? null,
        username: ci?.username ?? data.creator ?? null,
        profileImageUrl: ci?.profile_image_url ?? null,
      },
      createdAt: data.tree?.created_at ?? null,
      sightingsCount: data.tree?.sightings_count ?? 0,
      platformCount: distinctPlatformCount(sightings),
      childrenCount: Array.isArray(data.tree?.children) ? data.tree!.children!.length : 0,
    };
  } catch {
    return null;
  }
}
